import React, { Suspense, lazy, useState, useRef, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Bot, User, Square } from "lucide-react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import { API_URL, apiFetch } from "../../api/client";
import { getToken } from "../../api/token";
import { toolCalls } from "../../utils/mealChatThinking";
import ThinkingIndicator from "./ThinkingIndicator";

// react-markdown + remark-gfm pesan ~55 kB gzip y sólo hacen falta con el chat
// abierto: van en su propio chunk, precargado al abrir.
const loadChatMarkdown = () => import("./ChatMarkdown");
const ChatMarkdown = lazy(loadChatMarkdown);

// El chatbot de nutrición corre íntegramente sobre nuestro backend
// (POST /v1/chat, agente "nutrition"): historial multi-turno propiedad del
// servidor, streaming y persistencia en Postgres. El servidor es dueño del
// historial, por eso el cliente sólo envía el último mensaje.
const CONVERSATION_ID_KEY = "athletain_meal_chat_conversation_id";

const DEFAULT_BOT_GREETING =
  "¡Hola! Soy tu asistente de nutrición. Puedo ayudarte con recomendaciones de comidas, planes nutricionales, información sobre calorías y mucho más. ¿En qué puedo ayudarte hoy?";

// conversationId estable por navegador: se genera una vez y sobrevive recargas,
// de modo que el historial del servidor sigue asociado a la misma conversación.
const loadConversationId = () => {
  try {
    const existing = localStorage.getItem(CONVERSATION_ID_KEY);
    if (existing) return existing;
    const id = crypto.randomUUID();
    localStorage.setItem(CONVERSATION_ID_KEY, id);
    return id;
  } catch {
    // Modo privado / sin storage: id efímero para esta sesión.
    return crypto.randomUUID();
  }
};

// Aplana las partes de texto de un UIMessage a un string plano para las burbujas.
const messageText = (message) =>
  (message?.parts ?? [])
    .filter((part) => part?.type === "text" && typeof part.text === "string")
    .map((part) => part.text)
    .join("");

const MealChatbot = ({ isOpen, onClose }) => {
  const [conversationId] = useState(loadConversationId);
  const [inputMessage, setInputMessage] = useState("");
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const messagesEndRef = useRef(null);

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: `${API_URL}/v1/chat`,
        // El token se lee en cada request (Resolvable) para no capturar uno viejo.
        headers: () => {
          const token = getToken();
          return token ? { Authorization: `Bearer ${token}` } : {};
        },
        // El servidor es dueño del historial: sólo mandamos el último turno,
        // saneado al esquema estricto text-only del backend.
        prepareSendMessagesRequest: ({ id, messages }) => {
          const last = messages[messages.length - 1];
          return {
            body: {
              conversationId: id,
              agent: "nutrition",
              message: {
                id: last.id,
                role: "user",
                parts: [{ type: "text", text: messageText(last) }],
              },
            },
          };
        },
      }),
    [],
  );

  const { messages, sendMessage, status, stop, error, setMessages } = useChat({
    id: conversationId,
    transport,
  });

  const isBusy = status === "submitted" || status === "streaming";

  // "streaming" empieza con el primer chunk del stream, que suele ser una
  // llamada a herramienta y no texto: el indicador se basa en si ya hay texto
  // de la respuesta en curso, no en el status.
  const lastMessage = messages[messages.length - 1];
  const pendingReply = lastMessage?.role === "assistant" ? lastMessage : null;
  const isThinking = isBusy && !messageText(pendingReply);

  useEffect(() => {
    if (isOpen) loadChatMarkdown();
  }, [isOpen]);

  // Al abrir, sembramos los turnos previos desde el servidor (una sola vez).
  useEffect(() => {
    if (!isOpen || historyLoaded) return;
    let cancelled = false;

    (async () => {
      try {
        const data = await apiFetch(
          `/v1/chat/conversations/${conversationId}`,
        );
        if (!cancelled && Array.isArray(data?.messages)) {
          setMessages(data.messages);
        }
      } catch (err) {
        // 404 = conversación aún no creada: arrancamos vacíos con el saludo.
        if (!cancelled && err?.status !== 404) {
          // Otros errores no rompen el chat; el saludo estático se mantiene.
          console.warn("No se pudo cargar el historial del chat:", err?.message);
        }
      } finally {
        if (!cancelled) setHistoryLoaded(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, historyLoaded, conversationId, setMessages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isBusy]);

  const handleSend = () => {
    const text = inputMessage.trim();
    if (!text || isBusy) return;
    setInputMessage("");
    sendMessage({ text });
  };

  const handleStop = () => {
    // stop() corta el fetch del cliente; el POST /v1/chat/stop aborta la
    // generación del lado del servidor (que sigue drenando el stream).
    stop();
    apiFetch("/v1/chat/stop", {
      method: "POST",
      body: { conversationId },
    }).catch(() => {});
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-xl shadow-2xl w-full max-w-2xl h-[600px] max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-athletain-green text-white rounded-t-xl">
            <div className="flex items-center gap-3">
              <Bot size={24} />
              <div>
                <h3 className="font-semibold text-lg">
                  Asistente de Nutrición
                </h3>
                <p className="text-sm opacity-90">
                  Especialista en planes de comidas
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Saludo estático (no forma parte del historial del servidor) */}
            <div className="flex justify-start">
              <div className="flex items-start gap-3 max-w-[80%]">
                <div className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center bg-gray-100 text-gray-600">
                  <Bot size={16} />
                </div>
                <div className="rounded-lg px-4 py-2 bg-gray-100 text-gray-900">
                  <p className="text-sm whitespace-pre-wrap">
                    {DEFAULT_BOT_GREETING}
                  </p>
                </div>
              </div>
            </div>

            {messages.map((message) => {
              const isUser = message.role === "user";
              const text = messageText(message);
              if (!isUser && !text) return null;
              const isStreaming =
                !isUser && status === "streaming" && message === lastMessage;
              return (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${isUser ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`flex items-start gap-3 max-w-[80%] ${isUser ? "flex-row-reverse" : ""}`}
                  >
                    <div
                      className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center ${
                        isUser
                          ? "bg-[#203c79] text-white"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {isUser ? <User size={16} /> : <Bot size={16} />}
                    </div>
                    <div
                      className={`min-w-0 rounded-lg px-4 py-2 ${
                        isUser
                          ? "bg-[#203c79] text-white"
                          : "bg-gray-100 text-gray-900"
                      }`}
                    >
                      {isUser ? (
                        <p className="text-sm whitespace-pre-wrap">{text}</p>
                      ) : (
                        <Suspense
                          fallback={
                            <p className="text-sm whitespace-pre-wrap">{text}</p>
                          }
                        >
                          <ChatMarkdown text={text} streaming={isStreaming} />
                        </Suspense>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}

            <AnimatePresence>
              {isThinking && (
                <ThinkingIndicator
                  key="thinking"
                  calls={toolCalls(pendingReply)}
                />
              )}
            </AnimatePresence>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex justify-start"
              >
                <div className="flex items-start gap-3 max-w-[80%]">
                  <div className="w-8 h-8 shrink-0 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center">
                    <Bot size={16} />
                  </div>
                  <div className="rounded-lg px-4 py-2 bg-red-50 text-red-700">
                    <p className="text-sm whitespace-pre-wrap">
                      ❌ El asistente tuvo un problema. Inténtalo de nuevo.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Escribe tu pregunta sobre nutrición..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-athletain-blue focus:border-transparent"
                disabled={isBusy}
              />
              {isBusy ? (
                <button
                  onClick={handleStop}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                  title="Detener respuesta"
                >
                  <Square size={16} />
                </button>
              ) : (
                <button
                  onClick={handleSend}
                  disabled={!inputMessage.trim()}
                  className="px-4 py-2 bg-[#203c79] text-white rounded-lg hover:bg-[#1a3266] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  <Send size={16} />
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MealChatbot;
